import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  Chip,
  Paper,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import { commonRunQuery } from "../../util/API/loans";
import { PortfolioDownloadDocuments } from "../../util/API/portfolio";
import { decodeHtmlEntities } from "../../util/utilfunctions/utilFunctions";
const InvestmentDetail = ({ product, onBack, userRole }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [fundDetail, setFundDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  // const documents = [
  //   { name: 'Investor Presentation 2025', size: '2.4 MB' },
  //   { name: 'Offering Memorandum', size: '5.1 MB' },
  // ];

  const getInvestmentDetails = async () => {
    setLoading(true);
    try {
      const [detailResponse, docsResponse] = await Promise.all([
        commonRunQuery(360, product.id),
        commonRunQuery(361, product.id),
      ]);
      console.log(JSON.stringify(docsResponse));
      // --- Fund Detail ---
      const record = detailResponse?.data?.body?.record;
      if (record) {
        const f = record.f;
        const u = record.u;
        setFundDetail({
          id: f.id,
          name: f.name,
          description: f.description,
          totalFundSize: Number(f.total_fund_size),
          minimumTicketSize: Number(f.minimum_ticket_size),
          targetMultiple: f.target_multiple,
          targetYearlyReturn: f.target_yearly_return,
          tvpi: f.tvpi,
          dpi: f.dpi,
          rvpi: f.rvpi,
          videoUrl: f.associated_video_url,
          startDate: f.start_date,
          endDate: f.end_date,
          visibility: f.visibility,
          createdByRole: f.created_by_role,
          creatorName: `${u.creator_first_name} ${u.creator_last_name}`,
        });
      }

// --- Documents ---
const docRecords = docsResponse?.data?.body?.record;

if (docRecords) {
  // Normalize: single object ({documents: {...}}) OR array ([{documents: {...}}, ...])
  const docsArray = Array.isArray(docRecords) ? docRecords : [docRecords];

  setDocuments(
    docsArray.map((item) => ({
      id: item.documents.id,
      name: item.documents.file_name,
      type: item.documents.document_type.toUpperCase(),
      accessRole: item.documents.access_role,
      fileKey: item.documents.file_key,
      uploadedAt: item.documents.created_at,
    }))
  );
}
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInvestmentDetails();
  }, [product]);

  const formatCurrency = (value) =>
    `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Derive deal terms from API data
  const dealTerms = fundDetail
    ? [
        {
          label: "Minimum Investment",
          value: formatCurrency(fundDetail.minimumTicketSize),
        },
        {
          label: "Total Fund Size",
          value: formatCurrency(fundDetail.totalFundSize),
        },
        { label: "Target Multiple", value: `${fundDetail.targetMultiple}x` },
        {
          label: "Target Yearly Return",
          value: `${fundDetail.targetYearlyReturn}%`,
        },
        { label: "TVPI", value: fundDetail.tvpi },
        { label: "DPI", value: fundDetail.dpi },
        { label: "RVPI", value: fundDetail.rvpi },
        {
          label: "Visibility",
          value:
            fundDetail.visibility.charAt(0).toUpperCase() +
            fundDetail.visibility.slice(1),
        },
      ]
    : [];
const handleDownloadDocument = async (doc) => {
  try {
    const response = await PortfolioDownloadDocuments(doc.id);

    const accessUrl = response?.data?.payload?.downloadUrl || response?.payload?.downloadUrl;

    if (!accessUrl) {
      console.error("No access URL returned");
      return;
    }

    const cleanUrl = accessUrl.replace(/&amp;/g, '&').replace(/\s+/g, '');

    // ✅ Just open the presigned URL directly — no fetch needed
    // S3 presigned URL already has `response-content-disposition: attachment`
    // so it will trigger a download automatically
    window.open(cleanUrl, '_blank');

  } catch (error) {
    console.error("Download failed:", error);
  }
};
  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: "1300px",
        margin: "0 auto",
        bgcolor: "#fff",
      }}
    >
      {/* Header Navigation */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={onBack} sx={{ border: "1px solid #eee" }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight="800" color="#002e4c">
              {decodeHtmlEntities(fundDetail?.name) || decodeHtmlEntities(product.title)}
            </Typography>
            {fundDetail && (
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" color="textSecondary">
                  Managed by <strong>{fundDetail.creatorName}</strong> ·{" "}
                  {fundDetail.createdByRole}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Chip
          label="Active"
          size="small"
          color="success"
          sx={{ fontWeight: "bold" }}
        />
      </Box>

      <Grid container spacing={4}>
        {/* LEFT COLUMN */}
        <Grid item xs={12} md={8}>
          {/* Video Player / Placeholder */}
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              aspectRatio: "16/9",
              bgcolor: "#000",
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
              mb: 3,
            }}
          >
            {fundDetail?.videoUrl ? (
              <Box
                component="iframe"
                src={fundDetail.videoUrl}
                title="Fund Video"
                sx={{ width: "100%", height: "100%", border: "none" }}
                allowFullScreen
              />
            ) : (
              <Box sx={{ textAlign: "center", color: "#fff" }}>
                <PlayCircleOutlineIcon sx={{ fontSize: 80, opacity: 0.8 }} />
                <Typography>Video Presentation</Typography>
              </Box>
            )}
          </Paper>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
          >
            <Tab
              label="Overview"
              sx={{ fontWeight: "bold", textTransform: "none" }}
            />
            {/* <Tab
              label="Team"
              sx={{ fontWeight: "bold", textTransform: "none" }}
            />
            <Tab
              label="Updates"
              sx={{ fontWeight: "bold", textTransform: "none" }}
            />
            <Tab
              label="Q&A"
              sx={{ fontWeight: "bold", textTransform: "none" }}
            /> */}
          </Tabs>

          {/* Overview Tab */}
          {activeTab === 0 && fundDetail && (
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                About This Fund
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph>
                {decodeHtmlEntities(fundDetail.description)}
              </Typography>

              {/* Fund Duration */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 3,
                  p: 2,
                  bgcolor: "#f8f9fa",
                  borderRadius: 2,
                  border: "1px solid #eee",
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 18, color: "#002e4c" }} />
                <Typography variant="body2">
                  <strong>Fund Period:</strong>{" "}
                  {formatDate(fundDetail.startDate)} —{" "}
                  {formatDate(fundDetail.endDate)}
                </Typography>
              </Box>

              {/* Performance Metrics */}
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Performance Metrics
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  {
                    label: "TVPI",
                    value: `${fundDetail.tvpi}x`,
                    desc: "Total Value to Paid-In",
                  },
                  {
                    label: "DPI",
                    value: `${fundDetail.dpi}x`,
                    desc: "Distributions to Paid-In",
                  },
                  {
                    label: "RVPI",
                    value: `${fundDetail.rvpi}x`,
                    desc: "Residual Value to Paid-In",
                  },
                  {
                    label: "Target Return",
                    value: `${fundDetail.targetYearlyReturn}%`,
                    desc: "Yearly Target",
                  },
                  {
                    label: "Target Multiple",
                    value: `${fundDetail.targetMultiple}x`,
                    desc: "Investment Multiple",
                  },
                ].map((metric) => (
                  <Grid item xs={6} sm={4} key={metric.label}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "#f8f9fa",
                        borderRadius: 2,
                        border: "1px solid #eee",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="h6" fontWeight="900" color="#002e4c">
                        {metric.value}
                      </Typography>
                      <Typography variant="caption" fontWeight="bold">
                        {metric.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary"
                      >
                        {metric.desc}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Other tabs — placeholder */}
          {activeTab !== 0 && (
            <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
              <Typography>Content coming soon.</Typography>
            </Box>
          )}
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3} sx={{ position: "sticky", top: 20 }}>
            {/* Investment CTA Card */}
            <Paper
              variant="outlined"
              sx={{ p: 3, borderRadius: 4, bgcolor: "#fcfcfc" }}
            >
              <Typography variant="h4" fontWeight="900" color="#002e4c">
                {fundDetail ? formatCurrency(fundDetail.totalFundSize) : "—"}
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={2}>
                Total Fund Size
              </Typography>

              <Box
                sx={{
                  width: "100%",
                  height: 8,
                  bgcolor: "#eee",
                  borderRadius: 4,
                  mb: 3,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{ width: "85%", height: "100%", bgcolor: "#00bfa5" }}
                />
              </Box>
                {userRole !== 'admin' ? <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  bgcolor: "#002e4c",
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: "bold",
                  mb: 2,
                  "&:hover": { bgcolor: "#004a7a" },
                }}
              >
                Invest Now
              </Button> : ''}
              
              <Typography
                variant="caption"
                display="block"
                textAlign="center"
                color="textSecondary"
              >
                {fundDetail
                  ? formatCurrency(fundDetail.minimumTicketSize)
                  : "—"}{" "}
                minimum investment
              </Typography>
            </Paper>

            {/* Deal Terms */}
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                mb={1.5}
                display="flex"
                alignItems="center"
                gap={1}
              >
                Deal Terms <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </Typography>
              <Paper
                variant="outlined"
                sx={{ borderRadius: 3, overflow: "hidden" }}
              >
                {dealTerms.map((term, index) => (
                  <Box key={term.label}>
                    <Box
                      sx={{
                        p: 1.5,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        {term.label}
                      </Typography>
                      <Typography variant="caption" fontWeight="bold">
                        {term.value}
                      </Typography>
                    </Box>
                    {index < dealTerms.length - 1 && <Divider />}
                  </Box>
                ))}
              </Paper>
            </Box>

            {/* Documents */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
                Documents
              </Typography>
              <List sx={{ p: 0 }}>
                {documents.map((doc) => (
                  <ListItem
                    key={doc.id}
                    onClick={() => handleDownloadDocument(doc)}
                    sx={{
                      mb: 1,
                      borderRadius: 2,
                      border: "1px solid #eee",
                      "&:hover": { bgcolor: "#f5f5f5", cursor: "pointer" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <DescriptionIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={doc.name}
                      secondary={`${doc.type} · ${doc.accessRole} · ${new Date(doc.uploadedAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}`}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: "bold",
                      }}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InvestmentDetail;
